import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';
import {
  Button,
  DataTable,
  EmptyState,
  Icon,
  Panel,
  Stat,
  StatusDot,
  Tag,
  useErrorDialog,
  type Column,
} from '@dayon/design-system';
import { formatCount } from '@dayon/money';
import { api, ApiRequestError } from '../../lib/api.ts';
import { useSession } from '../../lib/session.tsx';
import { ExportDialog } from '../access/ExportDialog.tsx';
import { useAuditExport } from '../access/use-audit-export.ts';
import { ScreenHeader } from '../shell/ScreenHeader.tsx';
import { PARKED_NOTIFICATIONS_PATH } from '../funds/ParkedNotifications.tsx';
import { tileVerdict } from './health-verdict.ts';

/*
 * Backup & recovery USED to have no table and no route behind them at all, and
 * this screen rendered the gap by name rather than a fixture: a plausible
 * "Last full backup · 29 Aug 02:00 · verified" that nobody measured is a line
 * an operator stops worrying about.
 *
 * `platform.backup_run` (sql/860) is now that table, and `deploy/backup-db.sh`
 * is what writes to it — the host takes the backup, reads the archive back to
 * verify it, and records both. The panel shows what is actually there.
 *
 * WHAT IS STILL MISSING IS NARROWER, and the card says so where the button is:
 * this console cannot ASK the host to run one. That is pg_dump, a disk and a
 * retention sweep, and a route that shells out to them is a far larger surface
 * than a read. "Run backup" stays disabled for that reason rather than the old
 * one.
 */

/* ── Wire shapes ───────────────────────────────────────────────────────────
   snake_case, straight off the SELECT lists in
   services/api/src/modules/backoffice/platform.ts. Nothing is renamed on the
   way in. */

/** One row of reporting.system_health, latest observation per metric. */
interface HealthMetric {
  metric: string;
  value: string;
  meta: string | null;
  /** The FEED's verdict. Never recomputed here — see the note above. */
  ok: boolean;
  observed_at: string;
}

/** Counted live in SQL at request time, not read from the monitoring feed. */
interface PlatformCounts {
  failed_webhooks: string;
  stuck_transactions: string;
  audit_rows: string;
  /** max(seq) — null when platform.audit_log has never been written. */
  audit_seq: string | null;
}

/**
 * A row returned by platform.verify_audit_chain(). No rows means intact; each
 * row is a place the trail was reordered, removed or edited.
 */
interface ChainBreak {
  seq: number;
  expected_hash: string | null;
  stored_hash: string | null;
  problem: string;
}

interface HealthPayload {
  metrics: HealthMetric[];
  counts: PlatformCounts;
}

/**
 * One hash check conducted, as `GET /api/platform/hash-checks` returns it.
 *
 * `matchesPrevious` is computed server-side and is null where there is nothing
 * to compare against — a first check, or one of the two runs failed. Three
 * states, not a boolean, because "no answer yet" must not render as "changed".
 */
interface HashCheckRow {
  id: string;
  reason: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  algorithm: string;
  root_hash: string | null;
  file_count: number | null;
  total_bytes: string | null;
  revision: string | null;
  scope_root: string;
  exclusions: string;
  note: string | null;
  conducted_by: string | null;
  matchesPrevious: boolean | null;
}

const HASH_REASON_LABEL: Record<string, string> = {
  PRE_GO_LIVE: 'Before going live',
  REGULATOR_REQUEST: 'Regulator request',
  PERIODIC: 'Periodic',
  RELEASE: 'After a release',
};

interface AuditPayload {
  chainBreaks: ChainBreak[];
  intact: boolean;
}

interface BackupRun {
  kind: 'FULL' | 'INCREMENTAL' | 'RESTORE_TEST';
  status: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  started_at: string;
  finished_at: string | null;
  size_bytes: string | null;
  /**
   * When somebody read the file back. NULL means nobody has — and that is a
   * state the screen must be able to say out loud, because a dump nobody has
   * read is a file, not a backup.
   */
  verified_at: string | null;
  location: string | null;
  taken_by: string;
  note: string | null;
}

interface BackupPayload {
  latest: BackupRun[];
  /** How far back the chain reaches, measured rather than configured. */
  oldestRun: string | null;
  totalRuns: string;
}

/* ── Time ──────────────────────────────────────────────────────────────────
   "Timezone is display-only — all storage is UTC." */
const DISPLAY_TZ = 'Asia/Manila';

const BACKUP_KIND_LABEL: Record<string, string> = {
  FULL: 'Last full backup',
  INCREMENTAL: 'Last incremental',
  RESTORE_TEST: 'Last restore test',
};

/** Bytes as an operator reads them. Binary units, because disks are sold in
    decimal ones and the difference is what makes a figure look wrong. */
function formatBytes(raw: string): string {
  const n = Number(raw);
  if (!Number.isFinite(n) || n < 0) return '—';
  const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
  let value = n;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value < 10 && unit > 0 ? value.toFixed(1) : Math.round(value)} ${units[unit]}`;
}

const dayTimeFmt = new Intl.DateTimeFormat('en-GB', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: DISPLAY_TZ,
});

function dayTime(iso: string | null): string {
  return iso ? dayTimeFmt.format(new Date(iso)).replace(',', '') : '—';
}

/**
 * How old the observation is, on every tile. The verdict beside it is the
 * feed's own only while the reading is current: past the window in
 * health-verdict.ts the tile says how old the reading is instead of vouching
 * for it. That window judges the reading's age, never the figure.
 */
function age(iso: string): string {
  const minutes = Math.round((Date.now() - new Date(iso).getTime()) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

/**
 * The design reads latency → queue → lag → failures, worst-consequence last.
 * The API returns DISTINCT ON (metric) ordered by metric NAME, which is
 * alphabetical and meaningless. Presentation order is restored here; any metric
 * the feed adds later falls through and keeps the API's order.
 */
const TILE_ORDER = ['API p95 latency', 'Game round queue', 'DB replication lag', 'Failed jobs 24h'];

function shortHash(hash: string | null): string {
  return hash ? `${hash.slice(0, 12)}…` : 'none';
}

/**
 * The one table on this screen with a real source.
 *
 * It is normally empty, and that empty state is the point: it is the evidence
 * behind "Back-office audit log · 7 years, immutable".
 */
const chainColumns: Column<ChainBreak>[] = [
  {
    key: 'seq',
    header: 'Sequence',
    numeric: true,
    render: (b) => <span className="tabular">{b.seq}</span>,
  },
  {
    key: 'problem',
    header: 'Finding',
    render: (b) => (
      <>
        <StatusDot tone="attention" label="Integrity break" /> {b.problem}
      </>
    ),
  },
  {
    key: 'expected',
    header: 'Expected hash',
    secondary: true,
    render: (b) => <span className="tabular">{shortHash(b.expected_hash)}</span>,
  },
  {
    key: 'stored',
    header: 'Stored hash',
    secondary: true,
    render: (b) => <span className="tabular">{shortHash(b.stored_hash)}</span>,
  },
];

export function SystemAdmin() {
  const { brandId, brands: sessionBrands, session, allowed } = useSession();
  /**
   * The capability this module needs, read once and used in BOTH places that
   * need it: the refusal that decides what is painted, and the effects that
   * decide what is fetched. An early return covers only the first.
   */
  const canReadPlatform = allowed('platform.view');
  /* Conducting a hash check writes a row and runs a filesystem walk, so it sits
     behind the write capability. Reading the list needs only platform.view. */
  const canManagePlatform = allowed('platform.manage');
  /* Player funds, where the StarPay notifications filed for a person are listed, is read with this. */
  const canReadLedger = allowed('transactions.view');
  const { showError } = useErrorDialog();

  /*
   * Export logs, for real.
   *
   * This button used to open a modal offering a scope, a format and a delivery
   * channel, over a notice reading "There is no export route on the API and no
   * platform.export_run table to record the extract". Both halves of that
   * sentence stopped being true: POST /api/access/export hands a named spec to
   * the reports engine, which records the run and stores the artefact, and the
   * export writes its own AUDIT_LOG_EXPORTED row in the same transaction.
   *
   * The three selects went with the notice. Only the window was ever real, and
   * a format picker offering XLSX and PDF over a route that produces CSV is the
   * same category of lie the notice was. Shared with Access control through
   * `useAuditExport` so both screens run one act rather than two copies of it.
   */
  const auditExport = useAuditExport();

  const [metrics, setMetrics] = useState<HealthMetric[]>([]);
  const [counts, setCounts] = useState<PlatformCounts | null>(null);
  const [audit, setAudit] = useState<AuditPayload | null>(null);
  const [verifiedAt, setVerifiedAt] = useState<string | null>(null);
  /*
   * Whether each read FAILED, as distinct from returning nothing.
   *
   * Two independent reads, so two flags. Without them a refused
   * /api/platform/health left the header reading "0 health metrics · all
   * within tolerance" and the tile band saying the monitoring feed had
   * published nothing — a stopped feed is the one state this screen must not
   * render as calm, and an unanswered request is not even that. A refused
   * /api/platform/audit was worse still: the findings table falls through to
   * "Chain verified intact", which is a claim that somebody walked the chain.
   * Nobody did. And a single shared flag would be its own lie — the health
   * feed can answer perfectly while the chain walk times out.
   */
  const [healthFailed, setHealthFailed] = useState(false);
  const [auditFailed, setAuditFailed] = useState(false);
  const [healthLoading, setHealthLoading] = useState(true);
  const [backups, setBackups] = useState<BackupPayload | null>(null);
  const [backupsFailed, setBackupsFailed] = useState(false);
  const [auditLoading, setAuditLoading] = useState(true);
  const [hashChecks, setHashChecks] = useState<HashCheckRow[] | null>(null);
  const [hashChecksFailed, setHashChecksFailed] = useState(false);
  const [hashBusy, setHashBusy] = useState(false);

  // The chain walk is behind audit.export, not access.manage. Held as a boolean
  // so the effect depends on a stable value rather than on `allowed` itself.
  const canExportAudit = allowed('audit.export');

  useEffect(() => {
    /*
     * NOT ASKED, rather than asked and refused. The module's refusal below is a
     * RENDER decision and runs after every hook here was registered, so this read
     * still went out for a role the route refuses -- and the 403 raised a modal
     * claiming a failure on top of the notice explaining the refusal.
     */
    if (!canReadPlatform) {
      setHealthLoading(false);
      return;
    }
    let cancelled = false;

    // No brandId is sent. Neither platform route calls brandScope() — the
    // health feed and the audit chain are platform facts, not brand ones — so
    // passing the selector would be a filter the API silently ignores and the
    // heading would be claiming a scope that was never applied. (The audit
    // route does narrow its ENTRIES to the reader's own brand restriction; this
    // screen reads only its chain verdict, which covers the whole log for
    // every reader.)
    //
    // Two chains rather than one Promise.all: the tiles and the chain verdict
    // are separate claims behind separate routes and separate capabilities, and
    // a shared catch could only say that one of them failed. "The chain is
    // unverified" must not be reachable by the health feed timing out.
    setHealthLoading(true);
    api
      .get<HealthPayload>('/api/platform/health')
      .then((h) => {
        if (cancelled) return;
        setMetrics(h.metrics);
        setCounts(h.counts);
        setHealthFailed(false);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Cleared, not left standing: a tile from an earlier poll under a
        // failure notice is exactly the stale-but-current-looking figure an
        // on-call operator would trust.
        setMetrics([]);
        setCounts(null);
        setHealthFailed(true);
        showError({
          title: 'Could not load system health',
          message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
        });
      })
      .finally(() => !cancelled && setHealthLoading(false));

    /* A THIRD CHAIN, for the same reason there are already two: backups are a
       separate claim behind a separate route, and a shared catch could only
       say that something failed. A dead backup feed must never be able to make
       the health tiles look stale, or vice versa. */
    api
      .get<BackupPayload>('/api/platform/backups')
      .then((b) => {
        if (cancelled) return;
        setBackups(b);
        setBackupsFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        /* No error dialog: the rest of this screen is an on-call surface and a
           modal over it would stop somebody reading the health tiles they came
           for. The card says the read failed where the figure would be. */
        setBackups(null);
        setBackupsFailed(true);
      });

    /* A FOURTH CHAIN, for the reason the third one exists. Whether the deployed
       files have been hashed is its own claim behind its own route, and a dead
       feed here must not be able to make the health tiles look stale. */
    api
      .get<{ items: HashCheckRow[] }>('/api/platform/hash-checks')
      .then((h) => {
        if (cancelled) return;
        setHashChecks(h.items);
        setHashChecksFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setHashChecks(null);
        setHashChecksFailed(true);
      });

    if (canExportAudit) {
      setAuditLoading(true);
      api
        .get<AuditPayload>('/api/platform/audit')
        .then((a) => {
          if (cancelled) return;
          setAudit(a);
          // The chain is walked server-side on every read, so "verified" is the
          // moment this response landed — not a stored timestamp.
          setVerifiedAt(new Date().toISOString());
          setAuditFailed(false);
        })
        .catch((error: unknown) => {
          if (cancelled) return;
          setAudit(null);
          setVerifiedAt(null);
          setAuditFailed(true);
          showError({
            title: 'Could not verify the audit chain',
            message: error instanceof ApiRequestError ? error.message : 'Try again shortly.',
          });
        })
        .finally(() => !cancelled && setAuditLoading(false));
    } else {
      // Withheld by capability — neither verified nor failed. Named as such
      // below rather than folded into either.
      setAudit(null);
      setVerifiedAt(null);
      setAuditFailed(false);
      setAuditLoading(false);
    }

    return () => {
      cancelled = true;
    };
  }, [canReadPlatform, canExportAudit, showError]);

  const tiles = useMemo(() => {
    const rank = (m: HealthMetric) => {
      const i = TILE_ORDER.indexOf(m.metric);
      return i === -1 ? TILE_ORDER.length : i;
    };
    return metrics.toSorted((a, b) => rank(a) - rank(b));
  }, [metrics]);

  const newestObservation = useMemo(
    () =>
      metrics.reduce<string | null>(
        (latest, m) => (latest === null || m.observed_at > latest ? m.observed_at : latest),
        null,
      ),
    [metrics],
  );

  const scopedBrandName = useMemo(
    () => sessionBrands.find((b) => b.id === brandId)?.name ?? null,
    [sessionBrands, brandId],
  );

  /*
   * platform.view, not access.manage.
   *
   * This screen writes nothing, and walling it behind the two-factor
   * roles-and-users key had a second cost beyond the rule: the audit-chain
   * verdict below answers `audit.export` holders, and of the four roles that
   * then held audit.export only the Super Admin could open the screen it is
   * drawn on. Three roles held a capability with nowhere to exercise it.
   * platform.view is held by exactly the audit.export holders — those four,
   * and the PAGCOR Regulator — so the module and the panel on it agree about
   * who may look.
   */
  if (!canReadPlatform) {
    return (
      <EmptyState icon="lock-key" title="System administration">
        This module needs the platform.view capability. Your role does not hold it.
      </EmptyState>
    );
  }

  /**
   * Conduct a hash check now.
   *
   * PAGCOR item 44: a check "may be requested at any time" by EGLD or CMED, so
   * the operator standing in front of an inspector needs a button rather than a
   * runbook. PERIODIC is the reason a console-initiated check carries — the
   * other three are tied to paperwork this screen does not collect, and
   * REGULATOR_REQUEST additionally needs a reference the route refuses without.
   */
  const conductHashCheck = async (): Promise<void> => {
    setHashBusy(true);
    try {
      await api.post('/api/platform/hash-checks', { reason: 'PERIODIC' });
      const refreshed = await api.get<{ items: HashCheckRow[] }>('/api/platform/hash-checks');
      setHashChecks(refreshed.items);
      setHashChecksFailed(false);
    } catch (error: unknown) {
      showError({
        title: 'The hash check did not finish',
        message:
          error instanceof ApiRequestError
            ? error.message
            : 'No hash value was produced, and nothing on the platform was changed.',
        ...(error instanceof ApiRequestError ? { code: error.code } : {}),
      });
    } finally {
      setHashBusy(false);
    }
  };

  const healthBusy = healthLoading && metrics.length === 0;
  const auditBusy = auditLoading && audit === null;
  const attention = tiles.filter((m) => !m.ok);
  const breaks = audit?.chainBreaks ?? [];

  return (
    <>
      <ScreenHeader
        /* "System administration", though the sidebar says only "System admin".
           The rail shortens it to fit a narrow column; this screen has always
           said the longer form, and so does the capability refusal above — an
           operator told "your role does not hold it" under the heading "System
           administration" and then let in on a second day has to land on the
           same words, or the two paths look like two different places. */
        title="System administration"
        /* The sentence the file has been making to developers in comments since
           it was written, finally said to the person doing the work. It names
           the THREE things this screen is trusted on — the trail, the backups
           and the deployed files — because each one is a claim somebody has to
           be able to stand behind in front of a regulator, and none of them is
           obvious from the tile band that opens the screen. The notices below
           go on to say what each panel can and cannot prove; the purpose says
           who is standing here and why. */
        purpose="Where the administrator on call checks the platform is healthy and that its audit trail, backups and deployed files can still be trusted."
        /*
          The caption kept exactly as it was, all three states and the scope.

          It is tri-state and the three states are NOT interchangeable. "Loading…"
          means the health read has not come back yet; the first branch means it
          FAILED, so nothing below counted anything at all; and the counts mean
          the monitoring feed genuinely answered. Collapsing that middle state
          into a zero would turn an unanswered request into "all within
          tolerance" — on an on-call screen, the most expensive sentence in the
          console, and the one the whole file is arranged to avoid.

          "newest observation …" is appended only when there IS an observation,
          because an age computed from nothing would read as freshness. And
          "platform-wide" is unconditional: it comes from the fact that neither
          route here applies a brand filter, not from a read that can fail, so
          it is true even while the figures are still loading.
        */
        meta={
          <>
            {healthFailed
              ? 'The monitoring feed could not be read — nothing below is a verdict on this platform'
              : healthBusy
                ? 'Loading…'
                : `${formatCount(tiles.length)} health metrics · ${
                    attention.length === 0
                      ? 'all within tolerance'
                      : `${formatCount(attention.length)} needing attention`
                  }`}
            {newestObservation ? ` · newest observation ${age(newestObservation)}` : ''} ·
            platform-wide
          </>
        }
        /* Both buttons unchanged, including the reason the first one is dead.
           Run backup is still inert, and the reason has CHANGED — which is
           worth saying, because "no table" was the old one and is no longer
           true. Backups are now recorded and shown below. What this console
           cannot do is ASK THE HOST to take one: that is pg_dump, a disk and
           a retention sweep on the instance, and a route that shells out to
           them is a far larger surface than a read. The card below says so.
           Export logs is NOT inert — it runs the audit-trail extract through
           POST /api/access/export, which is why only this one is primary. */
        actions={
          <>
            <Button
              disabled
              aria-label="Run backup — this console records backups but cannot ask the host to take one"
            >
              <Icon name="database" />
              Run backup
            </Button>
            <Button
              variant="primary"
              onClick={auditExport.start}
              disabled={!canExportAudit}
              aria-label={
                canExportAudit ? 'Export logs' : 'Export logs — needs the audit.export capability'
              }
            >
              <Icon name="export" />
              Export logs
            </Button>
          </>
        }
      />

      <div className="notice notice-quiet">
        <Icon name="info" />
        <div>
          Every tile below is the monitoring feed&rsquo;s own figure — the same feed that pages
          on-call — so the ok / needs-attention state on each is the feed&rsquo;s verdict and never
          a threshold this console sets on the figure. A reading more than fifteen minutes old is too
          old to vouch for either way, so its tile says how old it is instead. Nothing on this screen is filtered by brand,
          so everything here is platform-wide
          {scopedBrandName ? `, including while the ${scopedBrandName} scope is selected` : ''}. The
          logs <em>on this screen</em> are operational: the immutable audit trail, with actor and
          before/after, is under <Link to="/access">Access control</Link> — and{' '}
          <strong>Export logs</strong> above extracts that trail, not these tiles, over a window you
          choose. It is scoped by your own brand restriction rather than by the selector, and
          running it appends two rows to the trail it exports.
        </div>
      </div>

      {healthBusy ? (
        <div className="skeleton" style={{ height: '104px' }}>
          &nbsp;
        </div>
      ) : /* The failed read is answered before either of the two states below,
             both of which describe a feed that actually replied.

             NEITHER STATE NAMES ITS SOURCE ANY MORE. The first used to open
             "GET /api/platform/health did not answer" and the second
             "reporting.system_health returned no observation" — the route and
             the table that back them, which is a detail for whoever is reading
             this file and useless to the administrator on call, who cannot
             query either one. The names are recorded here instead: the tiles
             and the counters both come from GET /api/platform/health, which
             serves the latest observation per metric out of
             reporting.system_health. What the operator is told is the thing
             they can act on — whether the figures in front of them were
             measured, and by whom. */
      healthFailed ? (
        <EmptyState icon="warning-circle" title="The health feed could not be read">
          The request for these figures did not come back, so no tile was drawn — this is a read
          that failed, not a silent feed and not a calm platform. The monitoring feed may be
          publishing perfectly normally; nothing on this screen can currently say either way, so
          treat the pager rather than this page as the source of truth until the read succeeds.
        </EmptyState>
      ) : tiles.length === 0 ? (
        <EmptyState icon="pulse" title="The monitoring feed has published nothing">
          The feed answered, and it had no observation to give — so this is a genuinely empty
          reading rather than a failed one, and nothing was filtered out to make it empty. That is
          not a healthy platform either: it is a feed that has stopped writing, which is the one
          state a health screen must not render as calm.
        </EmptyState>
      ) : (
        <div className="stat-grid">
          {tiles.map((m) => {
            /* The feed's verdict while the reading is current; once it is older than
               the window, how old it is and never "Within tolerance" (health-verdict.ts). */
            const verdict = tileVerdict(m.ok, m.observed_at);
            return (
              <Stat
                key={m.metric}
                label={m.metric}
                // "The value goes accent-coloured when the metric is not ok." The
                // colour never travels alone: the dot and the words below carry
                // the same state for anyone who cannot see it.
                value={
                  <span style={m.ok ? undefined : { color: 'var(--portal-accent-text)' }}>
                    {m.value}
                  </span>
                }
                delta={
                  <>
                    <StatusDot tone={verdict.tone} label={verdict.label} />{' '}
                    {m.meta ?? 'no detail published with this metric'}
                    {verdict.note}
                  </>
                }
                deltaUp={verdict.flagged}
              >
                <div className="text-subtle" style={{ fontSize: '11px' }}>
                  observed {age(m.observed_at)}
                </div>
              </Stat>
            );
          })}
        </div>
      )}

      <Panel title="Live platform counters">
        <div className="stack">
          <div className="notice notice-quiet">
            <Icon name="info" />
            <div>
              {/* platform.job_run (sql/700) keeps one row per job — its last completed run — and
                  no history, so the 24-hour failure count still cannot be broken down here. */}
              Counted from the platform&rsquo;s own records when this page loaded — not from the
              feed above, and not thresholded. The platform keeps only each background job&rsquo;s
              last completed run, not a history of runs, so the feed&rsquo;s{' '}
              <em>Failed jobs 24h</em> cannot be broken down into its webhooks and its exports here;
              the failed-webhook count is the nearest figure that can actually be counted, and the
              two are allowed to disagree because they measure different windows.
            </div>
          </div>
          {healthFailed ? (
            <EmptyState icon="warning-circle" title="The counters could not be read">
              These two arrive with the health readings, and that read failed. A zero on either
              would say no webhook has failed and no movement of money is stuck — neither of which
              was counted.
            </EmptyState>
          ) : healthBusy || !counts ? (
            <div className="skeleton" style={{ height: '88px' }}>
              &nbsp;
            </div>
          ) : (
            /*
            THE TWO SUB-LINES SAID WHERE THE FIGURE CAME FROM, BY TABLE NAME.
            They read "payment.webhook_inbox, status FAILED — all time" and
            "wallet.ledger_transaction still PENDING — a stuck movement of
            money", which told whoever wrote the SELECT exactly what they
            already knew and told the administrator on call nothing they could
            act on: neither name is a thing they can open, ask for or repeat to
            anybody.

            The names, for whoever reads this file: the first counts rows in
            `payment.webhook_inbox` with status FAILED, over all time rather
            than a window; the second counts rows in
            `wallet.ledger_transaction` still PENDING, and only those pending
            for more than an hour — the window lives in the SQL and is stated
            in the tile's own label, which is why the sub-line does not repeat
            it.

            WHAT HAD TO SURVIVE is the part an operator reads the sub-line for:
            WHICH figure this is (payment notifications, money movements), that
            it is a failure or a stall rather than a total, and over what span.
            Both sentences keep all three.
          */
            <div className="stat-grid">
              <Stat
                label="Failed payment webhooks"
                value={formatCount(Number(counts.failed_webhooks))}
                delta="payment notifications that failed — all time"
                deltaUp={Number(counts.failed_webhooks) > 0}
              />
              <Stat
                label="Ledger entries pending over an hour"
                value={formatCount(Number(counts.stuck_transactions))}
                delta="still pending in the money ledger — a stuck movement of money"
                deltaUp={Number(counts.stuck_transactions) > 0}
              />
            </div>
          )}
        </div>
      </Panel>

      {/* StarPay notifications filed for a person are the payout desk's (ruling 173): listed, and
          marked seen, on Player funds, in the tab its Payout provider tile opens
          (funds/ParkedNotifications.tsx). This screen, where they used to be, says where they went. */}
      <Panel title="Parked notifications">
        <div className="notice notice-quiet">
          <Icon name="info" />
          <div>
            StarPay notifications filed for a person are listed on Player funds, under Parked
            notifications — read with transactions.view, and marked seen with funds.approve.{' '}
            {canReadLedger ? (
              <Link to={PARKED_NOTIFICATIONS_PATH}>Open Parked notifications on Player funds</Link>
            ) : (
              <>
                {session?.role ?? 'This role'} does not hold transactions.view, so it cannot open
                them.
              </>
            )}
          </div>
        </div>
      </Panel>

      <Panel title="System logs · last hour">
        {/* The sentence below used to spell the missing table out as
            `platform.system_log (observed_at, level, service, message,
            request_id)` on a rolling window. That is the right shape and it is
            a note for whoever builds it, not for the administrator reading an
            empty panel at two in the morning — so it lives here now. The
            operator still gets the two facts they need: why this panel is empty
            today, and that filling it is a piece of work rather than a setting
            somebody forgot to switch on. */}
        <EmptyState icon="terminal-window" title="No operational log store behind this">
          Service events tagged with a level — errors, warnings and information — are not shown
          here. Nothing in this platform&rsquo;s own records keeps a service event with a time, a
          level and a message: the services send their logs to the separate logging product
          instead, which is the right place for them, so there is nothing to read from here. Showing them here means either reading back from that product or keeping
          a short-lived copy of recent events on this side — work in either case. Until one of the
          two exists this stays empty rather than replaying a fixture, and it would still not be the
          audit trail.
        </EmptyState>
      </Panel>

      <div className="halves">
        <Panel title="Data management">
          <div className="stack">
            {healthBusy ? (
              <div className="skeleton" style={{ height: '120px' }}>
                &nbsp;
              </div>
            ) : (
              <div className="stack">
                <dl className="def-row">
                  <dt>Back-office audit log</dt>
                  <dd>Can only be added to; each entry is chained to the one before</dd>
                </dl>
                <dl className="def-row">
                  <dt>Entries recorded</dt>
                  <dd>
                    {counts ? (
                      formatCount(Number(counts.audit_rows))
                    ) : healthFailed ? (
                      <span className="text-subtle">not read — the request failed</span>
                    ) : (
                      '—'
                    )}
                  </dd>
                </dl>
                <dl className="def-row">
                  <dt>Latest sequence</dt>
                  {/* "nothing written yet" is a real zero and stays exactly that
                    — but only where the count actually came back. */}
                  <dd>
                    {counts ? (
                      (counts.audit_seq ?? 'nothing written yet')
                    ) : healthFailed ? (
                      <span className="text-subtle">not read — the request failed</span>
                    ) : (
                      '—'
                    )}
                  </dd>
                </dl>
                <dl className="def-row">
                  <dt>Chain last verified</dt>
                  <dd>
                    {!canExportAudit ? (
                      <span className="text-subtle">needs audit.export</span>
                    ) : auditFailed ? (
                      // Never a verdict. An unwalked chain is not an intact one,
                      // and "0 findings" is what an unwalked chain looks like.
                      <span className="text-subtle">not verified — the chain walk failed</span>
                    ) : auditBusy || !audit ? (
                      <span className="text-subtle">walking the chain…</span>
                    ) : (
                      <>
                        {dayTime(verifiedAt)}{' '}
                        <Tag tone={audit.intact ? 'neutral' : 'outline'}>
                          {audit.intact
                            ? 'intact'
                            : `${formatCount(breaks.length)} finding${breaks.length === 1 ? '' : 's'}`}
                        </Tag>
                      </>
                    )}
                  </dd>
                </dl>
              </div>
            )}
            <div className="notice notice-quiet">
              <Icon name="warning-circle" />
              <div>
                Player data retention, Transaction retention, Game round archive and
                Right-to-erasure requests are <strong>not shown</strong>. Retention windows are
                licence-driven and shortening one needs compliance approval — a record carrying the
                subject, the window, the licence reference and the approver. Nothing on this
                platform holds one, and nothing sits behind &ldquo;2 open, 30-day SLA&rdquo; either;
                that needs erasure requests with a due date and a handler, which are equally
                unrecorded. Printing the four windows as text would state a licence position this
                console cannot check.
              </div>
            </div>
          </div>
        </Panel>

        {/*
         * FILE INTEGRITY — the hash check a regulator can ask for at any time.
         *
         * PAGCOR form OMD/Online-0060 Rev. 2 item 44, new in Revision 2: the
         * hash value goes to EGLD and CMED before live commercial operation, and
         * a check may be requested at any time by either.
         *
         * UNLIKE "Run backup" ABOVE, THIS BUTTON REALLY RUNS. Hashing needs a
         * directory walk and a digest, both in the API process, over files it
         * already has open — so there is no host surface to add and no reason to
         * make somebody do it by hand.
         */}
        <Panel title="File integrity">
          <div className="stack">
            {hashChecksFailed ? (
              <EmptyState icon="warning" title="The hash check record could not be read">
                Nothing came back at all, so whether the deployed files have ever been hashed is
                unknown to this screen rather than known to be no.
              </EmptyState>
            ) : hashChecks === null ? (
              <EmptyState icon="fingerprint" title="Reading the hash check record…">
                One moment.
              </EmptyState>
            ) : hashChecks.length === 0 ? (
              <EmptyState icon="fingerprint" title="No hash check has been conducted">
                PAGCOR requires the hash value of the deployed files before live commercial
                operation, and a check can be asked for at any time after that.{' '}
                <strong>Conduct one now</strong> walks the deployed files, hashes each, and records
                one value standing for all of them.
              </EmptyState>
            ) : (
              <>
                <dl className="detail-grid">
                  <div>
                    <dt>Last conducted</dt>
                    <dd>
                      {dayTime(hashChecks[0]!.started_at)}{' '}
                      {hashChecks[0]!.status !== 'SUCCEEDED' ? (
                        <Tag tone="outline">
                          {hashChecks[0]!.status === 'RUNNING' ? 'running' : 'failed'}
                        </Tag>
                      ) : /* THREE STATES, NOT A BOOLEAN. "Nothing to compare
                          against" must not render as "changed" — the first check
                          on a platform would otherwise look like a finding. */
                      hashChecks[0]!.matchesPrevious === null ? (
                        <Tag tone="neutral">first check</Tag>
                      ) : hashChecks[0]!.matchesPrevious ? (
                        <Tag tone="neutral">unchanged</Tag>
                      ) : (
                        <Tag tone="outline">files changed</Tag>
                      )}
                      <div className="text-subtle" style={{ fontSize: '11px' }}>
                        {HASH_REASON_LABEL[hashChecks[0]!.reason] ?? hashChecks[0]!.reason}
                        {hashChecks[0]!.conducted_by ? ` · ${hashChecks[0]!.conducted_by}` : ''}
                        {hashChecks[0]!.file_count !== null
                          ? ` · ${formatCount(hashChecks[0]!.file_count)} files`
                          : ''}
                      </div>
                    </dd>
                  </div>
                  <div>
                    <dt>Hash value</dt>
                    <dd>
                      {hashChecks[0]!.root_hash ? (
                        <>
                          {/* PRINTED IN FULL, and that is the point of the panel.
                            This is the string that gets handed to EGLD and CMED,
                            so a truncated display would send somebody to the
                            database to find the real one. */}
                          <span
                            className="tabular"
                            style={{ fontSize: '11px', wordBreak: 'break-all' }}
                          >
                            {hashChecks[0]!.root_hash}
                          </span>
                          <div className="text-subtle" style={{ fontSize: '11px' }}>
                            {hashChecks[0]!.algorithm} over every file in scope ·{' '}
                            {hashChecks[0]!.revision
                              ? `release ${hashChecks[0]!.revision.slice(0, 12)}`
                              : 'no release recorded'}
                          </div>
                        </>
                      ) : (
                        <span className="text-subtle">
                          no value — that check did not produce one
                        </span>
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>Checks on record</dt>
                    <dd>
                      {formatCount(hashChecks.length)}
                      <div className="text-subtle" style={{ fontSize: '11px' }}>
                        oldest shown {dayTime(hashChecks[hashChecks.length - 1]!.started_at)} · a
                        finished check cannot be edited afterwards
                      </div>
                    </dd>
                  </div>
                </dl>
              </>
            )}
            <div className="row header-spacer">
              <Button
                variant="primary"
                onClick={conductHashCheck}
                disabled={!canManagePlatform || hashBusy}
                aria-label={
                  canManagePlatform
                    ? 'Conduct a hash check now'
                    : 'Conduct a hash check — needs the platform.manage capability'
                }
              >
                <Icon name="fingerprint" />
                {hashBusy ? 'Hashing…' : 'Conduct a hash check'}
              </Button>
            </div>
            {/* The reason stated as visible text rather than left to a hover, for
              the rule this console holds everywhere: a control somebody cannot
              use says why. */}
            {!canManagePlatform && (
              <div className="notice notice-quiet header-spacer">
                <Icon name="lock-key" />
                <div>
                  Conducting a check needs the <strong>platform.manage</strong> capability. Your
                  role can read the record and not add to it.
                </div>
              </div>
            )}
            <div className="notice notice-quiet header-spacer">
              <Icon name="info" />
              <div>
                {/* The excluded folder is node_modules; the covered lock file is pnpm-lock.yaml. */}
                <strong>What is covered.</strong> Every file under the deployed application, except
                build caches, logs, test output and the downloaded third-party packages — which are
                excluded because they are rebuilt from the package lock file, and that file IS
                covered, pinning every dependency to an exact version. The scope and the exclusions are stored with
                each value, because a hash whose scope is unstated cannot be reproduced by whoever
                receives it. Download a check to get the per-file list, which is what turns a
                mismatch into something you can act on.
              </div>
            </div>
          </div>
        </Panel>

        <Panel title={'Backup & recovery'}>
          <div className="stack">
            {backupsFailed ? (
              <EmptyState icon="warning" title="The backup record could not be read">
                This is not an empty ledger — nothing came back at all, so whether the platform has
                been backed up is unknown to this screen rather than known to be no.
              </EmptyState>
            ) : backups === null ? (
              <EmptyState icon="hard-drives" title="Reading the backup record…">
                One moment.
              </EmptyState>
            ) : backups.latest.length === 0 ? (
              /* A REAL ZERO, and different from the failure above. The table
               exists and nothing has been recorded in it — which on a platform
               that takes backups by hand means nobody has run the recording
               script yet, not that no backup exists. Saying the first without
               the second would be its own kind of lie. The script is deploy/backup-db.sh,
               run on the instance. */
              <EmptyState icon="hard-drives" title="No backup has been recorded">
                The record exists and is empty. Backups taken by hand before this — and they have
                been — are not in it, because nothing recorded them. Running the backup script on
                the server takes one, checks it by reading it back, and records it here.
              </EmptyState>
            ) : (
              <dl className="detail-grid">
                {backups.latest.map((b) => (
                  <div key={b.kind}>
                    <dt>{BACKUP_KIND_LABEL[b.kind] ?? b.kind}</dt>
                    <dd>
                      {dayTime(b.started_at)}{' '}
                      {/* VERIFIED IS NOT THE SAME AS SUCCEEDED, and the two tones
                        keep them apart. A dump that finished and was never read
                        back is a file; the panel must not let it read as a
                        backup somebody can restore from. */}
                      {b.status !== 'SUCCEEDED' ? (
                        <Tag tone="outline">{b.status === 'RUNNING' ? 'running' : 'failed'}</Tag>
                      ) : b.verified_at ? (
                        <Tag tone="neutral">verified</Tag>
                      ) : (
                        <Tag tone="outline">not verified</Tag>
                      )}
                      <div className="text-subtle" style={{ fontSize: '11px' }}>
                        {b.size_bytes ? `${formatBytes(b.size_bytes)} · ` : ''}
                        {b.taken_by}
                        {b.note ? ` · ${b.note}` : ''}
                      </div>
                    </dd>
                  </div>
                ))}
                <div>
                  <dt>Retention</dt>
                  <dd>
                    {/* MEASURED, not configured. This is how far back the record
                      actually reaches, which is the only retention figure this
                      platform can stand behind — a policy number would be a
                      claim about a sweep nothing here observes. */}
                    {backups.oldestRun ? (
                      <>
                        back to {dayTime(backups.oldestRun)}
                        <div className="text-subtle" style={{ fontSize: '11px' }}>
                          {formatCount(Number(backups.totalRuns))} recorded · measured from the
                          record, not a configured policy
                        </div>
                      </>
                    ) : (
                      <span className="text-subtle">nothing recorded yet</span>
                    )}
                  </dd>
                </div>
              </dl>
            )}
            <div className="notice notice-quiet header-spacer">
              <Icon name="info" />
              <div>
                {/* Taking one is pg_dump, a disk and a retention sweep on the host. There is no
                    cron or timer calling deploy/backup-db.sh (docs/DEPLOYMENT.md, "Backups"), so the
                    old "never replaces the scheduled chain" described a schedule that does not
                    exist. */}
                <strong>This console records backups and cannot take one.</strong> Taking one
                means copying all of the platform&rsquo;s data on the server, room on its disk and clearing out
                old copies, so <strong>Run backup</strong>{' '}
                stays disabled — the one thing that button must never do is confirm a backup started
                when no request was sent. Nothing takes a backup on a schedule yet: each one is run
                by hand on the server, and <strong>it is not done until it verifies</strong>: the
                script reads the copy back before recording it, and anything it could not read shows here
                as <em>not verified</em> rather than disappearing.
              </div>
            </div>
          </div>
        </Panel>
      </div>

      <Panel title="Audit trail integrity">
        <div className="stack">
          {/*
          THE MECHANISM, WITHOUT THE NAMES OF ITS PARTS.

          This notice used to read "platform.audit_log refuses UPDATE and DELETE
          at the trigger and each row hashes the one before it, and
          platform.verify_audit_chain() walks the whole chain on every read of
          this endpoint". Every noun in it was correct and three of them were
          identifiers — a table, a database function and an HTTP route — which
          an auditor or an on-call administrator can neither look up nor quote
          to anybody. For whoever reads this file: the trail is
          `platform.audit_log`, its append-only guarantee is a trigger that
          rejects UPDATE and DELETE, each row carries the previous row's hash in
          `prev_hash`, and the walk is `platform.verify_audit_chain()`, run
          server-side on every GET /api/platform/audit.

          WHAT HAD TO SURVIVE is the whole reason the paragraph exists: that the
          refusal is structural rather than this console being careful, that the
          entries are tied to each other so a quiet edit cannot hide, and that
          the check is run fresh rather than remembered. All three are still
          here, in words an operator already has.
        */}
          <div className="notice notice-quiet">
            <Icon name="shield-check" />
            <div>
              Not the trail — the entries live under <Link to="/access">Access control</Link>, where
              toggling a capability takes effect at the user&rsquo;s next request, with no re-login,
              and is written to the audit log with before and after. What belongs here is the check
              on it: the platform itself refuses to let an entry be changed or deleted — underneath
              every screen, not only this one, so it cannot be got round — and every entry
              carries a fingerprint of the entry before it, which is why the whole chain has to be
              walked end to end, and is, every time this panel is read. A row below is a break, and
              a break means an entry was removed, reordered or edited.
            </div>
          </div>
          {!canExportAudit ? (
            <EmptyState icon="lock-key" title="Verification needs audit.export">
              Reading the chain is gated on the audit.export capability, which your role does not
              hold. The trail is still being written and still hash-chained — you simply cannot be
              shown the result of walking it.
            </EmptyState>
          ) : auditFailed ? (
            // The whole panel is evidence, and an empty findings table is the
            // shape that evidence takes. It must never be reachable by a request
            // that did not return: "no finding" and "nobody looked" are the same
            // blank table and opposite facts.
            <EmptyState icon="warning-circle" title="The chain was not walked">
              The request to walk the chain did not come back, so nothing was checked — no finding,
              and equally no assurance. The trail is still refusing to let an entry be changed or
              deleted, and every entry still carries the fingerprint of the one before it; what is
              missing is the walk itself, and until it succeeds this screen cannot tell you whether
              an entry was removed, reordered or edited. Try again shortly.
            </EmptyState>
          ) : auditBusy ? (
            <div className="skeleton" style={{ height: '120px' }}>
              &nbsp;
            </div>
          ) : (
            <DataTable
              caption="Audit chain integrity findings: sequence, what was found, and the expected against the stored hash"
              columns={chainColumns}
              rows={breaks}
              rowKey={(b) => String(b.seq)}
              empty={
                /* The walk is `platform.verify_audit_chain()` and the per-row
                 link is the `prev_hash` column; both were named here and are
                 now only in this comment. The claim is unchanged: it is the
                 evidence behind "7 years, immutable", and it says the check
                 ran at load rather than being remembered from earlier. */
                <EmptyState icon="shield-check" title="Chain verified intact">
                  The chain was walked when this page loaded and nothing was found
                  {counts ? `, across ${formatCount(Number(counts.audit_rows))} entries` : ''}.
                  Every entry still matches the fingerprint the next one keeps of it, and every
                  entry still matches its own contents. This is a live walk, not a cached verdict.
                </EmptyState>
              }
            />
          )}
        </div>
      </Panel>

      {/* The real audit-trail export, shared with Access control. */}
      <ExportDialog
        open={auditExport.open}
        onClose={auditExport.close}
        from={auditExport.from}
        to={auditExport.to}
        onFrom={auditExport.setFrom}
        onTo={auditExport.setTo}
        errors={auditExport.errors}
        busy={auditExport.busy}
        outcome={auditExport.outcome}
        /* The signed-in admin's OWN restriction, which is what an export is
           scoped by — not the brand SELECTOR at the top of the console, which
           this screen deliberately ignores. `session.brands` is the
           restriction; empty means every brand. */
        scopedBrandNames={(session?.brands ?? []).map((b) => b.name)}
        onSubmit={auditExport.submit}
        onDownload={auditExport.download}
      />
    </>
  );
}
